import React, { useState } from 'react';
import { CloudActions, CloudErrors } from '@portal/portal-core';

import { validateMinLength, validateMaxLength } from 'utils/validators';
import { FjInput, FjButton } from 'components/primitives';
import { alertsSelector, selectUserId } from 'selectors';
import { composeValidators, navigate } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import AuthContainer from '../auth-container';

const linkStyle = {
  color: '#156777',
  background: 'transparent',
  borderColor: 'transparent',
};

const buttonStyle = {
  defaultStyle: {
    background: '#fff',
    color: '#156777',
    borderColor: '#156777',
  },
  hoverStyle: {
    background: '#156777',
    color: '#fff',
    borderColor: '#156777',
  },
};

interface ChangePasswordForm {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

// The shape we consume from `composeValidators`' return value, matching `FjInput`'s
// `validator`/`getValidationMessage` props.
interface ComposedValidators {
  validator: (val: string) => boolean;
  getValidationMessage: (val: string) => string;
}

// The parameter shape `composeValidators` expects. The `utils/validators` builders produce
// `(val: string) => boolean`, which is not assignable to this `(val: unknown) => boolean`
// under `strictFunctionTypes`, so each argument is cast to this widened validator shape.
interface Validator {
  validator: (val: unknown) => boolean;
  getValidationMessage: (val: unknown) => string | unknown[];
}

const ChangePassword = (): React.JSX.Element => {
  // `selectUserId`/`alertsSelector` are dynamic lodash-path selectors typed as `unknown`.
  const userId = useAppSelector(selectUserId) as string;
  const alerts = useAppSelector(alertsSelector) as Record<string, unknown>;
  const dispatch = useAppDispatch();

  const [form, setForm] = useState<ChangePasswordForm>({
    currentPassword: '',
    password: '',
    confirmPassword: '',
  });

  const onValueChange = (key: keyof ChangePasswordForm, value: string): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const isValid = (): boolean => {
    const { currentPassword, password, confirmPassword } = form;
    return (
      currentPassword.length >= 8 &&
      currentPassword.length <= 256 &&
      password.length >= 8 &&
      password.length <= 256 &&
      password === confirmPassword
    );
  };

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    if (isValid()) {
      dispatch(CloudActions.changePassword(userId, form.currentPassword, form.password));
    }
  };

  const renderAlerts = (): React.JSX.Element | null => {
    if (alerts && alerts[CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD]) {
      return (
        <div className="fj-error-container margin-bottom-1">
          <span className="fj-prevalidation" data-test="reset-confirmation-error-expired">
            Request was invalid. Check to make sure current password was entered correctly.
          </span>
        </div>
      );
    }
    return null;
  };

  const renderCurrentPassword = (): React.JSX.Element => {
    const validators = composeValidators(
      validateMinLength(8) as Validator,
      validateMaxLength(256) as Validator,
    ) as ComposedValidators;
    return (
      <FjInput
        type="password"
        id="current-password"
        autoComplete="current-password"
        value={form.currentPassword}
        onChange={(val: string) => onValueChange('currentPassword', val)}
        placeholder="Current Password"
        borderColor="#156777"
        className="full-width margin-bottom-1"
        height={35}
        validator={validators.validator}
        getValidationMessage={validators.getValidationMessage}
      />
    );
  };

  const renderNewPassword = (): React.JSX.Element => {
    const validators = composeValidators(
      validateMinLength(8) as Validator,
      validateMaxLength(256) as Validator,
    ) as ComposedValidators;
    return (
      <FjInput
        type="password"
        id="new-password"
        autoComplete="new-password"
        value={form.password}
        onChange={(val: string) => onValueChange('password', val)}
        placeholder="New Password"
        borderColor="#156777"
        className="full-width margin-bottom-1"
        height={35}
        validator={validators.validator}
        getValidationMessage={validators.getValidationMessage}
      />
    );
  };

  const renderConfirmNewPassword = (): React.JSX.Element => {
    const validators = composeValidators(
      validateMinLength(8) as Validator,
      validateMaxLength(256) as Validator,
      {
        validator: (val: string) => val === form.password,
        getValidationMessage: () => 'Value does not match new password.',
      } as Validator,
    ) as ComposedValidators;
    return (
      <FjInput
        type="password"
        id="confirm-new-password"
        autoComplete="new-password"
        value={form.confirmPassword}
        onChange={(val: string) => onValueChange('confirmPassword', val)}
        placeholder="Confirm New Password"
        borderColor="#156777"
        className="full-width margin-bottom-1"
        height={35}
        validator={validators.validator}
        getValidationMessage={validators.getValidationMessage}
      />
    );
  };

  const renderSubmitButton = (): React.JSX.Element => (
    <FjButton
      defaultStyle={buttonStyle.defaultStyle}
      hoverStyle={buttonStyle.hoverStyle}
      type="submit"
      disabled={!isValid()}
      className="full-width font-size-1-em margin-bottom-sm"
    >
      Submit
    </FjButton>
  );

  const renderReturnToProfile = (): React.JSX.Element => (
    <FjButton
      defaultStyle={buttonStyle.defaultStyle}
      hoverStyle={buttonStyle.hoverStyle}
      type="button"
      onClick={() => navigate('/')}
      className="full-width font-size-1-em margin-bottom-sm"
    >
      Return to My Account
    </FjButton>
  );

  const renderReturnToProfileLink = (): React.JSX.Element => (
    <FjButton
      defaultStyle={linkStyle}
      hoverStyle={linkStyle}
      type="button"
      onClick={() => navigate('/')}
      height={20}
      width={50}
    >
      Return to My Account
    </FjButton>
  );

  if (alerts && alerts[CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD]) {
    return (
      <AuthContainer title="Change Password">
        <span className="fj-grey-font margin-bottom-1">Your password was changed.</span>
        {renderReturnToProfile()}
      </AuthContainer>
    );
  }

  return (
    <AuthContainer title="Change Password">
      <h5 className="fj-grey-font margin-bottom-med">Please enter your password.</h5>
      <form
        className="display-flex flex-column flex-align-center full-width"
        onSubmit={handleSubmit}
        data-test="change-password-form"
      >
        {renderCurrentPassword()}
        {renderNewPassword()}
        {renderConfirmNewPassword()}
        {renderAlerts()}
        {renderSubmitButton()}
        {renderReturnToProfileLink()}
      </form>
    </AuthContainer>
  );
};

export default ChangePassword;
